import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { API } from "../config/api.js";
import { get, post, del, put } from "../lib/http.js";
import { format } from "date-fns";
import { StarIcon as SolidStar } from "@heroicons/react/24/solid";
import FancyLoader from "../components/FancyLoader.jsx";
import { FaEdit } from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import AuthModal from "./AuthModal.jsx";
import logo from "../assets/logo2.png";
import { absMediaUrl, formatEventScheduleLabel, getEventScheduleParts, isEventEnded } from "../api/events.js";
import { formatDayLabel, ticketDisplayName, ticketDateFromType } from "../listYourShow/scheduleHelpers.js";
import {
  coupleTicketBadge,
  isAllDaysTicket,
  isCoupleTicket,
  isGroupTicket,
  isTicketSaleActive,
  personsPerUnit,
} from "../lib/ticketCouple.js";
import {
  DEFAULT_OG,
  SITE_NAME,
  getEventPageUrl,
  getEventShareDescription,
  getEventShareImage,
  getEventShareUrl,
  getWhatsAppShareUrl,
} from "../lib/eventShare.js";

/* ---------- tiny helpers ---------- */
const currencyINR = (n) =>
  typeof n === "number" ? `₹${n.toLocaleString("en-IN")}` : n || "—";

function timeAgo(dateString) {
  const now = new Date();
  const past = new Date(dateString);
  const diff = Math.floor((now - past) / 1000);
  if (diff < 60) return `${diff} seconds ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)} minutes ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)} days ago`;
  if (diff < 31536000) return `${Math.floor(diff / 2592000)} months ago`;
  return `${Math.floor(diff / 31536000)} years ago`;
}

/* ---------- minimal icons ---------- */
function IconBack() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <polyline points="15 18 9 12 15 6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function CalendarIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" strokeWidth="2" />
      <line x1="16" y1="2" x2="16" y2="6" strokeWidth="2" />
      <line x1="8" y1="2" x2="8" y2="6" strokeWidth="2" />
      <line x1="3" y1="10" x2="21" y2="10" strokeWidth="2" />
    </svg>
  );
}
function StarIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" className="text-amber-500">
      <path d="M12 .587l3.668 7.431L24 9.748l-6 5.854L19.335 24 12 20.201 4.665 24 6 15.602 0 9.748l8.332-1.73z" />
    </svg>
  );
}
function StarHalfIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" className="text-amber-500">
      <defs>
        <linearGradient id="half">
          <stop offset="50%" stopColor="currentColor" />
          <stop offset="50%" stopColor="transparent" />
        </linearGradient>
      </defs>
      <path
        d="M12 .587l3.668 7.431L24 9.748l-6 5.854L19.335 24 12 20.201 4.665 24 6 15.602 0 9.748l8.332-1.73z"
        fill="url(#half)"
      />
    </svg>
  );
}
function MapPinIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 1 1 18 0z" strokeWidth="2" />
      <circle cx="12" cy="10" r="3" strokeWidth="2" />
    </svg>
  );
}
function UsersIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <path d="M17 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2" strokeWidth="2" />
      <circle cx="9" cy="7" r="4" strokeWidth="2" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" strokeWidth="2" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" strokeWidth="2" />
    </svg>
  );
}
function ArrowRight() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
      <line x1="5" y1="12" x2="19" y2="12" strokeWidth="2" />
      <polyline points="12 5 19 12 12 19" strokeWidth="2" />
    </svg>
  );
}
function HeartIcon({ filled }) {
  return filled ? (
    <svg width="16" height="16" viewBox="0 0 24 24" className="text-rose-600" fill="currentColor">
      <path d="M12 21s-6.716-4.646-9.193-7.122C-.86 11.045.38 6.3 4.243 5.343A4.997 4.997 0 0 1 12 7c1.657-2.875 5.657-2.657 7.757-.557 3.864.957 5.103 5.702 1.436 8.535C18.716 16.354 12 21 12 21z" />
    </svg>
  ) : (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" className="text-rose-600" stroke="currentColor">
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 1 0-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 0 0 0-7.78z" strokeWidth="2" />
    </svg>
  );
}
function ClockIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
      <circle cx="12" cy="12" r="9" strokeWidth="1.8" />
      <path d="M12 7v5l3 2" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}
function HourglassIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
      <path d="M6 3h12M6 21h12M8 3v4l4 4-4 4v4M16 3v4l-4 4 4 4v4" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function AgeIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
      <circle cx="12" cy="8" r="3.5" strokeWidth="1.8" />
      <path d="M5 20v-1a5 5 0 0 1 5-5h4a5 5 0 0 1 5 5v1" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}
function LangIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
      <circle cx="12" cy="12" r="9" strokeWidth="1.8" />
      <path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" strokeWidth="1.8" />
    </svg>
  );
}
function ShareIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
      <circle cx="18" cy="5" r="2.5" strokeWidth="1.8" />
      <circle cx="6" cy="12" r="2.5" strokeWidth="1.8" />
      <circle cx="18" cy="19" r="2.5" strokeWidth="1.8" />
      <path d="M8.4 13.2l7.2 4.2M15.6 6.6l-7.2 4.2" strokeWidth="1.8" />
    </svg>
  );
}
function PhoneTicketIcon() {
  return (
    <svg width="48" height="48" viewBox="0 0 64 64" fill="none" aria-hidden>
      <rect x="22" y="8" width="20" height="36" rx="3" stroke="#b45309" strokeWidth="2.5" fill="#fff7ed" />
      <path d="M28 40h8" stroke="#b45309" strokeWidth="2" strokeLinecap="round" />
      <path d="M12 42c6 4 14 6 20 6s14-2 20-6" stroke="#f59e0b" strokeWidth="3" strokeLinecap="round" />
      <path d="M18 36c2.5 1.5 5 2.5 8 3" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function formatDurationLabel(mins) {
  const n = Number(mins);
  if (!Number.isFinite(n) || n <= 0) return null;
  if (n < 10) {
    // treat small numbers as hours (legacy form)
    const h = Math.floor(n);
    const m = Math.round((n - h) * 60);
    if (m) return `${h}h ${m}m`;
    return `${h}h`;
  }
  const h = Math.floor(n / 60);
  const m = Math.round(n % 60);
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
}

const toISTDate = (input) => {
  if (!input) return null;
  if (input instanceof Date) return input;
  const s = String(input);
  const fixed = /Z$/i.test(s) ? s.replace(/Z$/i, "+05:30") : s;
  return new Date(fixed);
};

export default function EventDetailsPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const location = useLocation()

  const [events, setEvents] = useState(null);
  const [selectedKey, setSelectedKey] = useState(null);
  const [selectedTicketDate, setSelectedTicketDate] = useState("");

  const [showModal, setShowModal] = useState(false);
  const [stars, setStars] = useState(0);
  const [hoverStars, setHoverStars] = useState(0);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);

  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState({ value: 0, count: 0 });

  const [showAllReviews, setShowAllReviews] = useState(false);
  const [showPopup, setShowPopup] = useState(false);

  const [slideIdx, setSlideIdx] = useState(0);
  const touchRef = useRef({ x: 0 });

  const [editingReview, setEditingReview] = useState(null); // review being edited

  // ---- NEW: interested state ----
  const [isInterested, setIsInterested] = useState(false);
  const [interestCount, setInterestCount] = useState(0);
  const [interestBusy, setInterestBusy] = useState(false);
  const [aboutExpanded, setAboutExpanded] = useState(false);
  const [termsExpanded, setTermsExpanded] = useState(false);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const leftScrollRef = useRef(null);

  /* ---------------- current user (from localStorage) ---------------- */
  const currentUser = useMemo(() => {
    try {
      const raw = JSON.parse(localStorage.getItem("bb_user"));
      if (!raw) return null;
      // normalize shape: prefer parsed.id then parsed._id
      const id = raw?.id ?? raw?._id ?? raw?.userId ?? null;
      const isAdmin = raw?.isAdmin || raw?.role === "admin" || raw?.roles?.includes?.("admin");
      return { raw: raw, id, isAdmin, token: raw?.token };
    } catch (e) {
      return null;
    }
  }, []);

  /* ---------------- helper: has current user already posted a review ---------------- */
  const userHasReviewed = useMemo(() => {
    if (!currentUser?.id || !reviews?.length) return false;
    return reviews.some((r) => String(r.userId?._id ?? r.userId) === String(currentUser.id));
  }, [currentUser, reviews]);

  const isAdmin = currentUser?.isAdmin === true;

  /* Resolved, de-duplicated absolute URLs so <img src> can be used directly */
  const images = useMemo(() => {
    const raw = Array.isArray(events?.landsacpImage)
      ? events.landsacpImage
      : events?.landsacpImage
        ? [events.landsacpImage]
        : [];
    const seen = new Set();
    const out = [];
    for (const item of raw) {
      const url = absMediaUrl(item);
      if (!url || seen.has(url)) continue;
      seen.add(url);
      out.push(url);
    }
    return out;
  }, [events?.landsacpImage]);

  useEffect(() => {
    setSlideIdx(0);
  }, [id, images]);

  useEffect(() => {
    if (images.length <= 1) return;
    const t = setInterval(() => setSlideIdx((i) => (i + 1) % images.length), 3500);
    return () => clearInterval(t);
  }, [images.length]);

  const goPrev = () => setSlideIdx((i) => (i - 1 + images.length) % images.length);
  const goNext = () => setSlideIdx((i) => (i + 1) % images.length);
  const onTouchStart = (e) => (touchRef.current.x = e.touches[0].clientX);
  const onTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - touchRef.current.x;
    if (Math.abs(dx) > 40) (dx > 0 ? goPrev() : goNext());
  };

  /* ---------------- Back button ---------------- */
  const handleBack = () => {
    nav("/");
  };

  /** Book Now → dedicated booking page (date → ticket → review) */
  const startBooking = () => {
    if (!events?._id) return;
    nav(`/book/${events._id}`);
  };

  const handleCancel = () => setShowPopup(false);
  const handleLoginRedirect = () => {
    setShowPopup(false);
    const target = `${location.pathname}${location.search || ""}`;
    // nav(`/login?next=${encodeURIComponent(target)}`, { state: { background: location }});
    openAuth("login", window.location.pathname + window.location.search);
  };
  const [authTab, setAuthTab] = useState("login"); // 'login' | 'register'
  const [nextAfterAuth, setNextAfterAuth] = useState("/");
  const [authOpen, setAuthOpen] = useState(false);
  const openAuth = (tab = "login", next = "/") => {
    setAuthTab(tab);
    setNextAfterAuth(next);
    setAuthOpen(true);
  };

  /* ---------------- ticket tiers ---------------- */
  const ticketDates = useMemo(() => {
    const list = Array.isArray(events?.ticketCategories) ? events.ticketCategories : [];
    const dates = [];
    const seen = new Set();
    for (const t of list) {
      if (!isTicketSaleActive(t)) continue;
      // Prefer explicit date; recover from type suffix if date was cleared by bug
      const d =
        String(t?.date || "").trim() ||
        ticketDateFromType(t?.type || "") ||
        "";
      if (!d || seen.has(d)) continue;
      // Skip pure season passes (no date, validFor all_days)
      if (!String(t?.date || "").trim() && isAllDaysTicket(t, events) && !ticketDateFromType(t?.type || "")) {
        continue;
      }
      seen.add(d);
      dates.push(d);
    }
    dates.sort();
    return dates;
  }, [events]);

  /** Season / all-days passes currently on sale — kept via isAllDaysTicket in availableTiers */
  const availableTiers = useMemo(() => {
    const list = Array.isArray(events?.ticketCategories)
      ? events.ticketCategories
      : [];
    return list.filter((t) => {
      if (!isTicketSaleActive(t)) return false;
      const recoveredDate =
        String(t?.date || "").trim() || ticketDateFromType(t?.type || "");
      // Season pass (no day) always visible alongside day filter
      if (!recoveredDate && isAllDaysTicket(t, events)) return true;
      if (!ticketDates.length) return true;
      if (!selectedTicketDate) return true;
      return recoveredDate === selectedTicketDate;
    });
  }, [events, ticketDates, selectedTicketDate]);

  const cheapestTier = useMemo(() => {
    if (!availableTiers.length) return null;
    const sorted = [...availableTiers].sort(
      (a, b) => Number(a?.price ?? 0) - Number(b?.price ?? 0)
    );
    return sorted[0] ?? null;
  }, [availableTiers]);

  const selectedTier = useMemo(() => {
    if (!availableTiers.length) return null;
    const found = availableTiers.find(
      (t) => (t.key ?? t.type) === selectedKey
    );
    if (found) return found;
    return cheapestTier;
  }, [availableTiers, selectedKey, cheapestTier]);

  const startingFrom = useMemo(() => {
    return cheapestTier ? Number(cheapestTier.price || 0) : 0;
  }, [cheapestTier]);

  /* ---------------- initial data ---------------- */
  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const token = currentUser?.token;
        const res = await get(`${API.events.getSinglEvent}/${id}`, {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
            "Content-Type": "application/json",
          },
        });
        if (!isMounted || !res?.event) return;
        const ev = res.event;
        setEvents(ev);

        const dated = (ev.ticketCategories || [])
          .filter((t) => isTicketSaleActive(t) && !isAllDaysTicket(t, ev))
          .map((t) => String(t?.date || "").trim())
          .filter(Boolean)
          .sort();
        const firstDate = dated[0] || "";
        if (firstDate) setSelectedTicketDate(firstDate);

        const pool = (ev.ticketCategories || []).filter((t) => {
          if (!isTicketSaleActive(t)) return false;
          if (isAllDaysTicket(t, ev)) return true;
          if (!firstDate) return true;
          return String(t?.date || "") === firstDate;
        });
        const firstAvailable =
          pool.find((t) => Number(t.availableSeats ?? 0) > 0) || pool[0];
        const firstKey = firstAvailable ? firstAvailable.key ?? firstAvailable.type : null;
        setSelectedKey(firstKey);
      } catch (err) {
        if (isMounted) console.error("getSinglEvent error:", err);
      }
    })();
    return () => { isMounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  /* ---------------- interest: count + my status ---------------- */
  useEffect(() => {
    if (!events?._id) return;
    (async () => {
      try {
        const cntRes = await get(API.interest.count(events._id));
        setInterestCount(Number(cntRes?.count ?? 0));

        const tokenFromState = currentUser?.token;
        const tokenFromStorage = JSON.parse(localStorage?.getItem('bb_user'))

        const token = tokenFromState ?? tokenFromStorage?.token;
        if (token) {
          const my = await get(API.interest.my, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const found = Array.isArray(my?.events)
            ? my.events.some((ev) => String(ev._id) === String(events._id))
            : false;
          setIsInterested(found);
        } else {
          setIsInterested(false);
        }
      } catch (e) {
        console.error("interest init error:", e);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events?._id]);

  const toggleInterested = async () => {
    // const token = currentUser?.token;
    const tokenFromState = currentUser?.token;
    const tokenFromStorage = JSON.parse(localStorage?.getItem('bb_user'))

    const token = tokenFromState ?? tokenFromStorage?.token;

    if (!token) {
      setShowPopup(true);
      return;
    }
    if (!events?._id || interestBusy) return;

    setInterestBusy(true);
    const prev = isInterested;
    // optimistic update
    setIsInterested(!prev);
    setInterestCount((c) => (prev ? Math.max(0, c - 1) : c + 1));

    try {
      if (!prev) {
        // mark
        await post(
          API.interest.mark(events._id),
          { notifyOn: ["tickets_live", "reminder"], source: "web" },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        // unmark
        await del(API.interest.unmark(events._id), {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
      }
    } catch (err) {
      console.error("toggleInterested error:", err);
      // rollback on error
      setIsInterested(prev);
      setInterestCount((c) => (prev ? c + 1 : Math.max(0, c - 1)));
      alert(
        err?.response?.data?.message ||
        err?.message ||
        "Failed to update interest"
      );
    } finally {
      setInterestBusy(false);
    }
  };

  /* ---------------- reviews + average ---------------- */
  const loadReviews = useCallback(async () => {
    try {
      // const token = currentUser?.token;
      const tokenFromState = currentUser?.token;
      const tokenFromStorage = JSON.parse(localStorage?.getItem('bb_user'))

      const token = tokenFromState ?? tokenFromStorage?.token;
      const res = await get(`${API.review.getReviews}/${id}`, {
        headers: { Authorization: token ? `Bearer ${token}` : undefined, "Content-Type": "application/json" },
      });
      if (res?.reviews) setReviews(res.reviews);
    } catch (err) {
      console.error("getReviews error:", err);
    }
  }, [id, currentUser?.token]);

  const loadAverageRating = useCallback(async () => {
    try {
      // const token = currentUser?.token;
      const tokenFromState = currentUser?.token;
      const tokenFromStorage = JSON.parse(localStorage?.getItem('bb_user'))

      const token = tokenFromState ?? tokenFromStorage?.token;
      const res = await get(`${API.review.averageRating}/${id}`, {
        headers: { Authorization: token ? `Bearer ${token}` : undefined, "Content-Type": "application/json" },
      });
      setRating({ value: res?.averageRating ?? 0, count: res?.reviewCount ?? 0 });
    } catch (err) {
      console.error("averageRating error:", err);
    }
  }, [id, currentUser?.token]);

  useEffect(() => {
    (async () => {
      await loadReviews();
      await loadAverageRating();
    })();
  }, [loadReviews, loadAverageRating]);

  /* ---------------- venue/map derived ---------------- */
  const venue = events?.venue || {};
  const lat = useMemo(() => {
    const raw = venue.lat ?? venue.latitude;
    return Number.isFinite(Number(raw)) ? Number(raw) : null;
  }, [venue.lat, venue.latitude]);
  const lng = useMemo(() => {
    const raw = venue.lon ?? venue.lng ?? venue.longitude;
    return Number.isFinite(Number(raw)) ? Number(raw) : null;
  }, [venue.lon, venue.lng, venue.longitude]);

  const fullAddress = useMemo(
    () => [venue.address, venue.city, venue.state].filter(Boolean).join(", "),
    [venue.address, venue.city, venue.state]
  );
  const placeQuery = useMemo(() => encodeURIComponent(fullAddress || venue.city || venue.name || "India"), [fullAddress, venue.city, venue.name]);
  const hasCoords = Number.isFinite(lat) && Number.isFinite(lng);
  const hasPlace = Boolean(fullAddress || venue.city || venue.name);

  const [mapMode, setMapMode] = useState("place");
  const placeEmbedSrc = hasCoords
    ? `https://www.google.com/maps?q=${lat},${lng}&z=15&output=embed`
    : `https://www.google.com/maps?q=${placeQuery}&z=12&output=embed`;
  const directionsEmbedSrc = hasCoords
    ? `https://www.google.com/maps?output=embed&dirflg=d&saddr=My+Location&daddr=${lat},${lng}`
    : `https://www.google.com/maps?output=embed&dirflg=d&saddr=My+Location&daddr=${placeQuery}`;
  const mapSrc = mapMode === "directions" ? directionsEmbedSrc : placeEmbedSrc;

  const handleGetDirections = (e) => {
    e.preventDefault();
    if (!hasCoords && !hasPlace) return;
    setMapMode("directions");
  };

  /* Desktop: lock page scroll so only the left column scrolls (BMS-style). */
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const prevHtmlOverflow = document.documentElement.style.overflow;
    const prevBodyOverflow = document.body.style.overflow;

    const apply = () => {
      if (mq.matches) {
        document.documentElement.style.overflow = "hidden";
        document.body.style.overflow = "hidden";
      } else {
        document.documentElement.style.overflow = prevHtmlOverflow || "";
        document.body.style.overflow = prevBodyOverflow || "";
      }
    };

    apply();
    mq.addEventListener?.("change", apply);
    mq.addListener?.(apply);
    return () => {
      mq.removeEventListener?.("change", apply);
      mq.removeListener?.(apply);
      document.documentElement.style.overflow = prevHtmlOverflow || "";
      document.body.style.overflow = prevBodyOverflow || "";
    };
  }, []);

  /* ---------------- loading state ---------------- */
  if (!events) {
    return (
      <main className="w-full px-4 py-10 grid place-items-center">
        <FancyLoader label="Processing..." />
      </main>
    );
  }

  /* ---------- helper: open review modal (create) ---------- */
  const openCreateReviewModal = () => {
    if (!currentUser?.token) {
      setShowPopup(true);
      return;
    }
    if (userHasReviewed) {
      alert("You have already submitted a review for this event.");
      return;
    }
    setEditingReview(null);
    setStars(0);
    setText("");
    setShowModal(true);
  };

  /* ---------- helper: open edit modal ---------- */
  const openEditReviewModal = (review) => {
    setShowAllReviews(false);
    if (!currentUser?.token) {
      setShowPopup(true);
      return;
    }
    const userId = currentUser?.id;
    const isOwner = userId && String(review.userId?._id || review.userId) === String(userId);
    if (!isOwner && !isAdmin) {
      alert("You can only edit your own review.");
      return;
    }
    setEditingReview(review);
    setStars(review.rating ?? 0);
    setText(review.comment ?? "");
    setShowModal(true);
  };

  /* ---------- submit (create or update) ---------- */
  const handleSubmitReview = async () => {

    if (!stars || !text.trim()) {
      alert("Please give a rating and write a review");
      return;
    }
    try {
      setLoading(true);
      // const token = currentUser?.token;
      const tokenFromState = currentUser?.token;
      const tokenFromStorage = JSON.parse(localStorage?.getItem('bb_user'))

      const token = tokenFromState ?? tokenFromStorage?.token;
      const userId = currentUser?.id;
      if (!token) {
        alert("Login required");
        setShowPopup(true);
        return;
      }

      if (editingReview) {
        const payload = { eventId: events._id, userId, rating: stars, comment: text };
        const res = await put(`${API.review.editReviews}/${editingReview._id}`, payload, {
          headers: { Authorization: token ? `Bearer ${token}` : undefined, "Content-Type": "application/json" },
        });
        // if backend returns success message or updated object, refresh list
        await Promise.all([loadReviews(), loadAverageRating()]);
        setShowModal(false);
        setEditingReview(null);
        setStars(0);
        setText("");
      } else {
        const payload = { eventId: events._id, userId, rating: stars, comment: text };
        await post(API.review.create, payload, {
          headers: { Authorization: token ? `Bearer ${token}` : undefined, "Content-Type": "application/json" },
        });
        await Promise.all([loadReviews(), loadAverageRating()]);
        setShowModal(false);
        setStars(0);
        setText("");
      }
    } catch (err) {
      console.error("Error submitting review", err);
      const serverMsg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Failed to submit review";
      alert(serverMsg);
    } finally {
      setLoading(false);
    }
  };

  /* ---------- delete review ---------- */
  const handleDeleteReview = async (review) => {
    if (!currentUser?.token) {
      setShowPopup(true);
      return;
    }
    const userId = currentUser?.id;
    const isOwner = userId && String(review.userId?._id || review.userId) === String(userId);
    if (!isOwner && !isAdmin) {
      alert("You can only delete your own review.");
      return;
    }
    if (!confirm("Are you sure you want to delete this review?")) return;

    try {
      setLoading(true);
      const res = await del(`${API.review.deleteReviews}/${review._id}`, {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${currentUser.token}` },
      });
      if (res?.success || res?.ok || res?.deleted) {
        setReviews((prev) => prev.filter((r) => String(r._id) !== String(review._id)));
        await loadAverageRating();
      } else {
        // If backend returns a message
        await loadReviews();
        await loadAverageRating();
      }
    } catch (err) {
      console.error("Delete review error:", err);
      alert(err?.message || "Failed to delete review");
    } finally {
      setLoading(false);
    }
  };

  /* ---------- helper render stars ---------- */
  const renderStars = (val) => {
    const arr = [];
    const full = Math.floor(val);
    const half = val % 1 >= 0.5;
    for (let i = 0; i < full; i++) arr.push(<StarIcon key={`f-${i}`} />);
    if (half) arr.push(<StarHalfIcon key="half" />);
    while (arr.length < 5) arr.push(<StarIcon key={`e-${arr.length}`} />);
    return arr;
  };

  const EmptyReviews = () => (
    <div className="rounded-2xl bg-white dark:bg-slate-950 transition-colors border shadow-sm dark:shadow-none p-8 text-center">
      <div className="text-2xl font-semibold mb-2">No reviews yet</div>
      <p className="text-sm text-slate-600 dark:text-slate-400 transition-colors mb-4">Be the first to share your experience — customers value honest feedback.</p>
      <div className="flex items-center justify-center gap-3">
        <button onClick={openCreateReviewModal} className="rounded-xl bg-amber-400 px-4 py-2 font-semibold text-slate-900 dark:text-white transition-colors">
          Write the first review
        </button>
      </div>
    </div>
  );

  const isPastEvent = isEventEnded(events);

  /* ---------------- derived: detail page bits ---------------- */
  const categoryTags = (() => {
    const raw = [
      events?.category,
      events?.subcategory,
      ...(Array.isArray(events?.subcategories) ? events.subcategories : []),
      ...(Array.isArray(events?.genres) ? events.genres : []),
    ];
    const seen = new Set();
    const out = [];
    for (const item of raw) {
      const label = String(item || "").trim();
      if (!label) continue;
      const key = label.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(label);
      if (out.length === 4) break;
    }
    return out;
  })();

  const galleryImages = (() => {
    const folderImages = (Array.isArray(events?.gallery) ? events.gallery : []).flatMap((g) =>
      Array.isArray(g?.images) ? g.images : []
    );
    const seen = new Set();
    const out = [];
    for (const raw of [...folderImages, ...images]) {
      const url = absMediaUrl(raw) || raw;
      if (!url || typeof url !== "string" || seen.has(url)) continue;
      seen.add(url);
      out.push(url);
    }
    return out;
  })();

  const performers = (Array.isArray(events?.performers) ? events.performers : []).filter((p) =>
    String(p?.name || "").trim()
  );

  const durationLabel = formatDurationLabel(events?.duration);

  const languageLabel =
    Array.isArray(events?.languages) && events.languages.filter(Boolean).length
      ? events.languages.filter(Boolean).join(" / ")
      : String(events?.language || "").trim() || null;

  const ageLabel = Number(events?.ageRestriction) > 0 ? `${events.ageRestriction} yrs +` : null;

  const showtimeLabel = (() => {
    const parts = getEventScheduleParts(events);
    if (parts?.time) return parts.time;
    const first = (Array.isArray(events?.showtimes) ? events.showtimes : []).find((s) => s?.startTime);
    if (!first?.startTime) return null;
    const d = new Date(first.startTime);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleTimeString("en-GB", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
      timeZone: "UTC",
    });
  })();

  const mapsExternalUrl = hasCoords
    ? `https://www.google.com/maps?q=${lat},${lng}`
    : `https://www.google.com/maps/search/?api=1&query=${placeQuery}`;

  const anyAvailable = availableTiers.some((t) => Number(t?.availableSeats ?? 0) > 0);

  const shareEvent = async () => {
    const eventId = events?._id || id;
    const url = getEventShareUrl(eventId);
    const title = events?.name ? `${events.name} | ${SITE_NAME}` : SITE_NAME;
    const text = `${getEventShareDescription(events)}\n${url}`;

    // Mobile: try system share (with poster image when allowed)
    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      try {
        const imageUrl = getEventShareImage(events);
        if (imageUrl && navigator.canShare) {
          try {
            const res = await fetch(imageUrl, { mode: "cors" });
            if (res.ok) {
              const blob = await res.blob();
              const ext = (blob.type || "").includes("png") ? "png" : "jpg";
              const file = new File([blob], `showtimebro-event.${ext}`, {
                type: blob.type || "image/jpeg",
              });
              if (navigator.canShare({ files: [file] })) {
                await navigator.share({ title, text, url, files: [file] });
                return;
              }
            }
          } catch {
            /* CORS / network — fall back to link-only share */
          }
        }
        await navigator.share({ title, text, url });
        return;
      } catch (err) {
        if (err?.name === "AbortError") return;
      }
    }

    setShareCopied(false);
    setShareOpen(true);
  };

  const copyShareLink = async () => {
    const eventId = events?._id || id;
    const url = getEventShareUrl(eventId);
    try {
      await navigator.clipboard.writeText(url);
      setShareCopied(true);
      window.setTimeout(() => {
        setShareOpen(false);
        setShareCopied(false);
      }, 700);
    } catch {
      window.prompt("Copy this link:", url);
      setShareOpen(false);
    }
  };

  const closeShareSheet = () => {
    setShareOpen(false);
    setShareCopied(false);
  };

  const shareOnWhatsApp = () => {
    const eventId = events?._id || id;
    const title = events?.name ? `${events.name} | ${SITE_NAME}` : SITE_NAME;
    window.open(getWhatsAppShareUrl(eventId, title), "_blank", "noopener,noreferrer");
    setShareOpen(false);
  };

  const scrollToTickets = () => {
    const el = ticketsRef.current;
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const MetaRow = ({ icon, children }) => (
    <div className="flex items-start gap-3 text-sm text-slate-700 dark:text-slate-300 transition-colors">
      <span className="mt-0.5 shrink-0 text-slate-500 dark:text-slate-400 transition-colors">{icon}</span>
      <div className="min-w-0 flex-1">{children}</div>
    </div>
  );

  /* Rendered twice: inline on mobile and inside the sticky desktop aside */
  const bookingCard = (
    <div
      className="rounded-2xl border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-950 overflow-hidden shadow-[0_8px_32px_rgba(15,23,42,0.12)] dark:shadow-none"
      style={{ animation: "stbFadeUp 420ms cubic-bezier(0.22,1,0.36,1) both" }}
    >
      {/* Gold top strip */}
      <div className="h-1 bg-gradient-to-r from-amber-400 via-yellow-400 to-orange-400" />

      <div className="p-5 space-y-3">
        <MetaRow icon={<CalendarIcon />}>{formatEventScheduleLabel(events)}</MetaRow>
        {showtimeLabel && <MetaRow icon={<ClockIcon />}>{showtimeLabel}</MetaRow>}
        {durationLabel && <MetaRow icon={<HourglassIcon />}>{durationLabel}</MetaRow>}
        {ageLabel && <MetaRow icon={<AgeIcon />}>{ageLabel}</MetaRow>}
        {languageLabel && <MetaRow icon={<LangIcon />}>{languageLabel}</MetaRow>}
        {(venue?.name || venue?.city) && (
          <MetaRow icon={<MapPinIcon />}>
            <div className="font-medium text-slate-800 dark:text-slate-200">
              {[venue?.name, venue?.city].filter(Boolean).join(", ")}
            </div>
            <a href={mapsExternalUrl} target="_blank" rel="noreferrer"
              className="mt-0.5 inline-block text-xs font-semibold text-amber-600 hover:text-amber-700 hover:underline"
            >View on Map</a>
          </MetaRow>
        )}
      </div>

      <div className="border-t border-slate-100 dark:border-slate-800 px-5 py-4">
        {!isPastEvent ? (
          <>
            <div className="flex items-center justify-between gap-3 mb-4">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500 mb-0.5">Price</div>
                <div className="text-2xl font-extrabold text-slate-900 dark:text-white">
                  {!cheapestTier
                    ? "On request"
                    : Number(startingFrom) <= 0
                      ? "Free"
                      : `${currencyINR(startingFrom)}`}
                  {cheapestTier && Number(startingFrom) > 0 && (
                    <span className="text-sm font-normal text-slate-400 dark:text-slate-500 ml-1">onwards</span>
                  )}
                </div>
              </div>
              <span
                className={`rounded-full px-3 py-1 text-xs font-bold ${anyAvailable
                  ? "bg-emerald-100 dark:bg-emerald-950/50 text-emerald-700 dark:text-emerald-400"
                  : "bg-rose-100 dark:bg-rose-950/50 text-rose-700 dark:text-rose-400"}`}
              >
                {anyAvailable ? "✓ Available" : "Sold out"}
              </span>
            </div>

            <button
              type="button"
              onClick={startBooking}
              disabled={!anyAvailable}
              className="w-full rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 py-3.5 font-bold text-white shadow-md shadow-amber-200 dark:shadow-none transition hover:-translate-y-0.5 hover:from-amber-400 hover:to-orange-400 hover:shadow-lg active:translate-y-0 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
            >
              {anyAvailable ? "Book Now →" : "Sold Out"}
            </button>
          </>
        ) : (
          <div className="rounded-xl bg-slate-100 dark:bg-slate-900 px-4 py-3 text-center text-sm font-semibold text-slate-500 dark:text-slate-400">
            🎭 This event has ended
          </div>
        )}
      </div>
    </div>
  );

  /* =================================================== UI =================================================== */
  const pageUrl = getEventPageUrl(events?._id || id);
  const shareImage = getEventShareImage(events) || DEFAULT_OG;
  const shareDesc = getEventShareDescription(events);
  const shareTitle = events?.name ? `${events.name} | ${SITE_NAME}` : SITE_NAME;

  return (
    <>
      {/* Deep-link / social share metadata (browsers + in-app webviews) */}
      <title>{shareTitle}</title>
      <meta name="description" content={shareDesc} />
      <link rel="canonical" href={pageUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={shareTitle} />
      <meta property="og:description" content={shareDesc} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:image" content={shareImage} />
      <meta property="og:image:alt" content={events?.name || SITE_NAME} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={shareTitle} />
      <meta name="twitter:description" content={shareDesc} />
      <meta name="twitter:image" content={shareImage} />

      <style>{`
        @keyframes stbFadeUp {
          0% { opacity: 0; transform: translateY(16px) }
          100% { opacity: 1; transform: translateY(0) }
        }
        @keyframes popIn {
          0% { opacity: 0; transform: translateY(10px) scale(.97) }
          100% { opacity: 1; transform: translateY(0) scale(1) }
        }
        @keyframes stbFade {
          0% { opacity: 0 }
          100% { opacity: 1 }
        }
        @keyframes slideInRight {
          0% { opacity: 0; transform: translateX(20px) }
          100% { opacity: 1; transform: translateX(0) }
        }
        .event-detail-page { animation: stbFadeUp 400ms cubic-bezier(0.22,1,0.36,1) both; }
        .booking-card-anim { animation: slideInRight 420ms cubic-bezier(0.22,1,0.36,1) both; }
      `}</style>

      <section className="w-full bg-white dark:bg-slate-950 transition-colors font-sans lg:h-[calc(100dvh-4.5rem)] lg:overflow-hidden">
        <div className="mx-auto grid h-full max-w-7xl grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px] lg:overflow-hidden">

          {/* ── LEFT COLUMN ── */}
          <div
            ref={leftScrollRef}
            className="event-detail-page min-h-0 min-w-0 lg:h-full lg:overflow-y-auto lg:overscroll-contain [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
          >
            {/* HERO IMAGE CAROUSEL */}
            <div className="relative overflow-hidden bg-slate-950">
              {images.length > 0 ? (
                <div className="relative w-full" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
                  <img
                    key={slideIdx}
                    src={images[slideIdx]}
                    alt={`${events?.name || "Event"} ${slideIdx + 1}`}
                    className="block h-auto max-h-[420px] w-full object-contain animate-[stbFade_300ms_ease-out]"
                    loading="eager"
                  />
                  {images.length > 1 && (
                    <>
                      <button onClick={goPrev} className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full bg-black/40 backdrop-blur-sm p-2 text-white hover:bg-black/60 transition" aria-label="Previous">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"><polyline points="15 18 9 12 15 6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                      </button>
                      <button onClick={goNext} className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full bg-black/40 backdrop-blur-sm p-2 text-white hover:bg-black/60 transition" aria-label="Next">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"><polyline points="9 18 15 12 9 6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
                      </button>
                      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                        {images.map((_, i) => (
                          <button key={i} onClick={() => setSlideIdx(i)} aria-label={`Go to slide ${i + 1}`}
                            className={`h-1.5 rounded-full transition-all duration-300 ${slideIdx === i ? "w-6 bg-white" : "w-1.5 bg-white/50 hover:bg-white/80"}`}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className="flex min-h-[220px] w-full items-center justify-center text-sm text-slate-500 dark:text-slate-400 bg-slate-100 dark:bg-slate-900">
                  No image available
                </div>
              )}
            </div>

            {/* MAIN CONTENT */}
            <div className="px-4 py-5 md:px-6 space-y-6">

              {/* Title + Actions */}
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="mb-2.5 flex flex-wrap gap-1.5">
                    {categoryTags.map((tag) => (
                      <span key={tag} className="rounded-full bg-amber-100 dark:bg-amber-900/40 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wide text-amber-800 dark:text-amber-400">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <h1 className="text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white md:text-3xl leading-tight">
                    {events?.name}
                  </h1>
                  <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                    {rating.count > 0 && (
                      <span className="inline-flex items-center gap-1 font-semibold text-amber-500">
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"><path d="M12 .587l3.668 7.431L24 9.748l-6 5.854L19.335 24 12 20.201 4.665 24 6 15.602 0 9.748l8.332-1.73z"/></svg>
                        <span className="text-slate-800 dark:text-white">{Number(rating.value || 0).toFixed(1)}</span>
                        <span className="text-slate-400 font-normal">({rating.count})</span>
                      </span>
                    )}
                    {rating.count > 0 && <span className="text-slate-300 dark:text-slate-600">•</span>}
                    <span className="text-slate-500 dark:text-slate-400">
                      {Number(interestCount || 0).toLocaleString()} interested
                    </span>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <button
                    type="button" onClick={toggleInterested} disabled={interestBusy}
                    title={isInterested ? "Remove interest" : "I am interested"}
                    className={`flex items-center gap-1.5 rounded-full border px-3 py-2 text-sm font-semibold transition disabled:opacity-60
                      ${isInterested ? "border-rose-400 bg-rose-50 dark:bg-rose-950/40 text-rose-600" : "border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-rose-300 hover:bg-rose-50 dark:hover:bg-rose-950/30 hover:text-rose-600"}`}
                  >
                    <HeartIcon filled={!!isInterested} />
                    <span className="hidden sm:inline text-xs">{isInterested ? "Interested" : "Interested?"}</span>
                  </button>
                  <button type="button" onClick={shareEvent} title="Share" aria-label="Share this event"
                    className="rounded-full border border-slate-200 dark:border-slate-700 p-2 text-slate-500 dark:text-slate-400 transition hover:border-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950/30 hover:text-amber-600"
                  >
                    <ShareIcon />
                  </button>
                </div>
              </div>

              {/* QUICK INFO PILLS */}
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {formatEventScheduleLabel(events) && (
                  <div className="flex items-center gap-2 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800 px-3 py-2.5">
                    <span className="shrink-0 text-amber-500"><CalendarIcon /></span>
                    <span className="text-xs font-semibold text-slate-700 dark:text-slate-300 leading-tight">{formatEventScheduleLabel(events)}</span>
                  </div>
                )}
                {showtimeLabel && (
                  <div className="flex items-center gap-2 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800 px-3 py-2.5">
                    <span className="shrink-0 text-amber-500"><ClockIcon /></span>
                    <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">{showtimeLabel}</span>
                  </div>
                )}
                {durationLabel && (
                  <div className="flex items-center gap-2 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800 px-3 py-2.5">
                    <span className="shrink-0 text-amber-500"><HourglassIcon /></span>
                    <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">{durationLabel}</span>
                  </div>
                )}
                {(venue?.name || venue?.city) && (
                  <div className="flex items-center gap-2 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800 px-3 py-2.5">
                    <span className="shrink-0 text-amber-500"><MapPinIcon /></span>
                    <span className="text-xs font-semibold text-slate-700 dark:text-slate-300 truncate">{[venue?.name, venue?.city].filter(Boolean).join(", ")}</span>
                  </div>
                )}
                {languageLabel && (
                  <div className="flex items-center gap-2 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800 px-3 py-2.5">
                    <span className="shrink-0 text-amber-500"><LangIcon /></span>
                    <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">{languageLabel}</span>
                  </div>
                )}
                {ageLabel && (
                  <div className="flex items-center gap-2 rounded-xl bg-slate-50 dark:bg-slate-900 border border-slate-100 dark:border-slate-800 px-3 py-2.5">
                    <span className="shrink-0 text-amber-500"><AgeIcon /></span>
                    <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">{ageLabel}</span>
                  </div>
                )}
              </div>

              {/* MOBILE BOOKING CARD */}
              <div className="lg:hidden">{bookingCard}</div>

              {/* ABOUT */}
              {events?.description && (
                <div className="border-t border-slate-100 dark:border-slate-800 pt-5">
                  <h2 className="text-lg font-bold tracking-tight text-slate-900 dark:text-white">About The Event</h2>
                  <div className="relative mt-3">
                    <div
                      className={`overflow-hidden text-sm leading-relaxed text-slate-600 dark:text-slate-300 transition-[max-height] duration-500 ease-in-out [&_a]:font-semibold [&_a]:text-amber-600 [&_a]:underline [&_p]:my-2 [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5
                        ${aboutExpanded ? "max-h-[3000px]" : "max-h-36"}`}
                      dangerouslySetInnerHTML={{ __html: events.description }}
                    />
                    {!aboutExpanded && (
                      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-white dark:from-slate-950 to-transparent" aria-hidden />
                    )}
                  </div>
                  <button type="button" onClick={() => setAboutExpanded((v) => !v)}
                    className="mt-2 inline-flex items-center gap-1 text-sm font-bold text-amber-600 hover:text-amber-700 transition"
                  >
                    {aboutExpanded ? "Read Less ↑" : "Read More ↓"}
                  </button>
                </div>
              )}

              {/* ARTISTS */}
              {performers.length > 0 && (
                <div className="border-t border-slate-100 dark:border-slate-800 pt-5">
                  <h2 className="text-lg font-bold tracking-tight text-slate-900 dark:text-white">Artists</h2>
                  <div className="mt-4 flex gap-4 overflow-x-auto pb-2 no-scrollbar">
                    {performers.map((p, i) => {
                      const img = absMediaUrl(p?.imageUrl) || p?.imageUrl;
                      const roles = (Array.isArray(p?.roles) ? p.roles : []).filter(Boolean).join(", ");
                      return (
                        <div key={`${p.name}-${i}`} className="w-24 shrink-0 text-center">
                          {img ? (
                            <img src={img} alt={p.name}
                              className="h-24 w-24 rounded-2xl object-cover shadow-sm ring-2 ring-slate-100 dark:ring-slate-800 transition hover:scale-105"
                            />
                          ) : (
                            <div className="flex h-24 w-24 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-100 to-orange-200 dark:from-amber-900/60 dark:to-orange-900/60 text-xl font-black uppercase text-amber-700 dark:text-amber-400 shadow-sm transition hover:scale-105">
                              {String(p.name).trim().slice(0, 2)}
                            </div>
                          )}
                          <div className="mt-2 truncate text-xs font-bold text-slate-900 dark:text-white">{p.name}</div>
                          {roles && <div className="truncate text-[10px] text-slate-500 dark:text-slate-400">{roles}</div>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* M-TICKET BANNER */}
              <div className="flex items-center gap-4 rounded-2xl border border-amber-200/70 dark:border-amber-900/50 bg-gradient-to-r from-amber-50 to-orange-50/50 dark:from-amber-950/30 dark:to-orange-950/20 p-4">
                <div className="shrink-0 text-3xl">📱</div>
                <div className="min-w-0">
                  <div className="text-sm font-bold text-slate-900 dark:text-white">M-Ticket — No Printout Needed</div>
                  <p className="mt-0.5 text-xs text-slate-600 dark:text-slate-400">Just show the ticket on your phone at the venue entry and walk right in.</p>
                </div>
              </div>

              {/* GALLERY */}
              {galleryImages.length > 0 && (
                <div className="border-t border-slate-100 dark:border-slate-800 pt-5">
                  <h2 className="text-lg font-bold tracking-tight text-slate-900 dark:text-white">Gallery</h2>
                  <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-4">
                    {galleryImages.slice(0, 7).map((src, i) => (
                      <button key={`${src}-${i}`} type="button" onClick={() => setGalleryOpen(true)}
                        className="group aspect-square overflow-hidden rounded-xl bg-slate-100 dark:bg-slate-900"
                      >
                        <img src={src} alt={`Gallery ${i + 1}`} loading="lazy"
                          className="h-full w-full object-cover transition duration-300 group-hover:scale-110"
                        />
                      </button>
                    ))}
                    <button type="button" onClick={() => setGalleryOpen(true)}
                      className="flex aspect-square flex-col items-center justify-center rounded-xl border-2 border-dashed border-amber-300 dark:border-amber-700 bg-amber-50/60 dark:bg-amber-950/20 text-amber-700 dark:text-amber-400 transition hover:bg-amber-100 dark:hover:bg-amber-950/40"
                    >
                      <span className="text-xl">🖼</span>
                      <span className="mt-1 text-[10px] font-bold">View All</span>
                      <span className="text-[10px]">{galleryImages.length} photos</span>
                    </button>
                  </div>
                </div>
              )}

              {/* VENUE + MAP */}
              <div className="border-t border-slate-100 dark:border-slate-800 pt-5">
                <h2 className="mb-4 text-lg font-bold tracking-tight text-slate-900 dark:text-white">Venue</h2>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm">
                    {venue?.logoUrl ? (
                      <img src={absMediaUrl(venue.logoUrl) || venue.logoUrl} alt="" className="h-48 w-full object-cover"/>
                    ) : venue?.name ? (
                      <div className="flex h-48 w-full items-center justify-center bg-gradient-to-br from-amber-500 via-orange-500 to-amber-600 px-4 text-center text-2xl font-black uppercase tracking-tight text-white">
                        {venue.name}
                      </div>
                    ) : (lat && lng) ? (
                      <img src={`https://maps.googleapis.com/maps/api/staticmap?center=${lat},${lng}&zoom=15&size=800x400&markers=color:red|${lat},${lng}`} alt="Map preview" className="h-48 w-full object-cover"/>
                    ) : (
                      <div className="flex h-48 w-full items-center justify-center bg-slate-100 dark:bg-slate-800 text-slate-400 text-sm">No Image</div>
                    )}
                    <div className="p-4">
                      {venue?.name && (
                        <div className="flex items-center gap-2 font-bold text-slate-900 dark:text-white">
                          <MapPinIcon /> {venue.name}
                        </div>
                      )}
                      <div className="mt-1 text-sm text-slate-600 dark:text-slate-400">{fullAddress || "—"}</div>
                      <div className="mt-3 flex items-center justify-between">
                        {venue?.capacity && (
                          <div className="flex items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400">
                            <UsersIcon /> {venue.capacity} capacity
                          </div>
                        )}
                        {(hasCoords || hasPlace) ? (
                          <button onClick={handleGetDirections}
                            className="inline-flex items-center gap-1 text-xs font-bold text-amber-600 hover:text-amber-700 hover:underline transition"
                          >Directions <ArrowRight /></button>
                        ) : (
                          <span className="inline-flex cursor-not-allowed items-center gap-1 text-xs text-slate-400">Directions <ArrowRight /></span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="overflow-hidden rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm">
                    <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-800 px-4 py-2.5">
                      <span className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wide">
                        {mapMode === "place" ? "Map" : "Directions"}
                      </span>
                      {(hasCoords || hasPlace) && (
                        <div className="flex gap-1.5">
                          <button type="button" onClick={() => setMapMode("place")}
                            className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold transition ${mapMode === "place" ? "bg-amber-500 text-white" : "border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:border-amber-300"}`}
                          >Pin</button>
                          <button type="button" onClick={() => setMapMode("directions")}
                            className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold transition ${mapMode === "directions" ? "bg-amber-500 text-white" : "border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:border-amber-300"}`}
                          >Route</button>
                        </div>
                      )}
                    </div>
                    <div className="aspect-[16/10]">
                      {(hasCoords || hasPlace) ? (
                        <iframe key={mapSrc} title="Venue Map" src={mapSrc} className="h-full w-full" loading="lazy" referrerPolicy="no-referrer-when-downgrade" allowFullScreen />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-sm text-slate-400">Location not available</div>
                      )}
                    </div>
                    {(hasCoords || hasPlace) && (
                      <div className="px-4 py-2.5 border-t border-slate-100 dark:border-slate-800">
                        <a className="text-xs font-semibold text-amber-600 hover:underline"
                          href={hasCoords ? `https://www.google.com/maps?q=${lat},${lng}` : `https://www.google.com/maps/search/?api=1&query=${placeQuery}`}
                          target="_blank" rel="noreferrer"
                        >Open in Google Maps ↗</a>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* TERMS */}
              {Array.isArray(events?.termsAndConditions) && events.termsAndConditions.length > 0 && (
                <div className="border-t border-slate-100 dark:border-slate-800 pt-5">
                  <button type="button" aria-expanded={termsExpanded} aria-controls="event-terms-panel" onClick={() => setTermsExpanded((v) => !v)}
                    className="flex w-full items-center justify-between gap-2 text-left"
                  >
                    <h2 className="text-lg font-bold tracking-tight text-slate-900 dark:text-white">Terms & Conditions</h2>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                      className={`shrink-0 text-slate-500 dark:text-slate-400 transition-transform duration-200 ${termsExpanded ? "rotate-90" : ""}`}
                    >
                      <polyline points="9 18 15 12 9 6" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </button>
                  {termsExpanded && (
                    <ul id="event-terms-panel" className="mt-3 space-y-2 text-sm text-slate-600 dark:text-slate-300">
                      {events.termsAndConditions.map((t, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <span className="mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500"/>
                          <span>{t.point}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {/* REVIEWS */}
              <div className="border-t border-slate-100 dark:border-slate-800 pt-5 pb-8">
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="text-lg font-bold tracking-tight text-slate-900 dark:text-white">Reviews & Ratings</h2>
                  {reviews.length > 3 && (
                    <button onClick={() => setShowAllReviews(true)} className="text-sm font-bold text-amber-600 hover:underline">
                      View All ({reviews.length})
                    </button>
                  )}
                </div>

                <div className="rounded-2xl border border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-4 border-b border-slate-100 dark:border-slate-800">
                    <div className="flex items-center gap-3">
                      <div className="text-4xl font-extrabold text-slate-900 dark:text-white leading-none">{rating.value ? rating.value.toFixed(1) : "—"}</div>
                      <div>
                        <div className="flex items-center gap-0.5">{renderStars(rating.value)}</div>
                        <div className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{rating.count} {rating.count === 1 ? "review" : "reviews"}</div>
                      </div>
                    </div>
                    {!userHasReviewed ? (
                      <button onClick={openCreateReviewModal}
                        className="rounded-xl bg-amber-500 hover:bg-amber-400 px-4 py-2 text-sm font-bold text-white shadow-sm transition"
                      >Write Review</button>
                    ) : (
                      <span className="rounded-xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/30 px-3 py-1.5 text-xs font-semibold text-emerald-700 dark:text-emerald-400">You reviewed</span>
                    )}
                  </div>

                  <div className="divide-y divide-slate-100 dark:divide-slate-800">
                    {reviews.length === 0 ? (
                      <div className="px-4 py-8 text-center">
                        <div className="text-2xl mb-2">💬</div>
                        <div className="text-sm font-semibold text-slate-700 dark:text-slate-300">No reviews yet</div>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Be the first to share your experience!</p>
                        <button onClick={openCreateReviewModal} className="mt-3 rounded-xl bg-amber-500 px-4 py-2 text-sm font-bold text-white hover:bg-amber-400 transition">
                          Write the first review
                        </button>
                      </div>
                    ) : (
                      reviews.slice(0, 3).map((r, i) => {
                        const userId = currentUser?.id;
                        const isOwner = userId && String(r.userId?._id || r.userId) === String(userId);
                        const showActions = isOwner || isAdmin;
                        return (
                          <div key={r._id || i} className="flex gap-3 px-4 py-4">
                            {r.userId?.profileImage ? (
                              <img src={r.userId.profileImage} alt={r.userId?.name || "User"} className="h-9 w-9 rounded-full object-cover shrink-0"/>
                            ) : (
                              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-500 text-sm font-bold uppercase text-white">
                                {r.userId?.name?.substring(0, 2) || "U"}
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2">
                                <div>
                                  <span className="text-sm font-bold text-slate-900 dark:text-white">{r.userId?.name || "User"}</span>
                                  <span className="ml-2 text-xs text-slate-400">{timeAgo(r.createdAt)}</span>
                                  <div className="mt-0.5 flex items-center gap-0.5">{renderStars(r.rating || 0)}</div>
                                </div>
                                {showActions && (
                                  <div className="flex items-center gap-1.5 shrink-0">
                                    <button onClick={() => openEditReviewModal(r)}
                                      className="rounded-lg border border-slate-200 dark:border-slate-700 px-2 py-1 text-[11px] text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
                                    ><FaEdit /></button>
                                    <button onClick={() => handleDeleteReview(r)} disabled={loading}
                                      className="rounded-lg border border-rose-200 dark:border-rose-800/60 px-2 py-1 text-[11px] text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/30 transition"
                                    >{loading ? "..." : <MdDelete />}</button>
                                  </div>
                                )}
                              </div>
                              <p className="mt-1.5 text-sm text-slate-600 dark:text-slate-300 leading-relaxed">{r.comment}</p>
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* ── RIGHT COLUMN ── */}
          <aside className="booking-card-anim relative hidden min-w-0 self-start lg:block border-l border-slate-100 dark:border-slate-800 h-full overflow-y-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
            <div className="p-5 pt-6">
              {bookingCard}
            </div>
          </aside>
        </div>
      </section>

      {/* GALLERY LIGHTBOX */}
      {galleryOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <button
            onClick={() => setGalleryOpen(false)}
            aria-label="Close gallery"
            className="absolute inset-0"
          />
          <div className="relative max-h-[85vh] w-full max-w-4xl overflow-y-auto rounded-2xl bg-white dark:bg-slate-950 transition-colors p-5 shadow-2xl animate-[popIn_180ms_cubic-bezier(0.22,1,0.36,1)]">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-bold text-slate-900 dark:text-white transition-colors">Gallery</h3>
              <button
                onClick={() => setGalleryOpen(false)}
                className="rounded-full p-2 text-slate-500 dark:text-slate-400 transition-colors hover:bg-slate-100 dark:bg-slate-900 transition-colors hover:text-slate-800 dark:text-slate-200 transition-colors"
                aria-label="Close"
              >
                ✕
              </button>
            </div>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
              {galleryImages.map((src, i) => (
                <img
                  key={`${src}-lb-${i}`}
                  src={src}
                  alt={`Gallery ${i + 1}`}
                  loading="lazy"
                  className="aspect-[4/3] w-full rounded-lg object-cover"
                />
              ))}
            </div>
          </div>
        </div>
      )}


      {/* WRITE / EDIT REVIEW MODAL */}
      {showModal && (
        <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/50">
          <div className="relative w-[92vw] max-w-md rounded-2xl border border-amber-100/70 bg-white dark:bg-slate-950 transition-colors p-6 shadow-2xl animate-[popIn_180ms_cubic-bezier(0.22,1,0.36,1)]">
            <button onClick={() => { setShowModal(false); setEditingReview(null); }} className="absolute right-3 top-3 text-slate-500 dark:text-slate-400 transition-colors hover:text-slate-700 dark:text-slate-300 transition-colors">✕</button>
            <h3 className="mb-4 text-lg font-bold text-slate-900 dark:text-white">{editingReview ? "Edit your review" : "Write a Review"}</h3>

            <div className="mb-4 flex justify-center gap-2">
              {[1, 2, 3, 4, 5].map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setStars(s)}
                  onMouseEnter={() => setHoverStars(s)}
                  onMouseLeave={() => setHoverStars(0)}
                  className="focus:outline-none"
                >
                  <SolidStar className={`h-8 w-8 cursor-pointer transition-colors ${s <= (hoverStars || stars) ? "text-amber-400" : "text-slate-300 dark:text-slate-700"}`} />
                </button>
              ))}
            </div>

            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              className="w-full rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-3 text-sm text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-amber-300 dark:focus:ring-amber-500 transition-colors"
              placeholder="Write your review here..."
              rows="4"
            />

            <div className="mt-4 flex justify-end gap-3">
              <button onClick={() => { setShowModal(false); setEditingReview(null); }} className="rounded-full border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 px-4 py-2 text-sm font-semibold transition-colors">
                Cancel
              </button>
              <button
                onClick={handleSubmitReview}
                disabled={loading || stars === 0}
                className="rounded-full bg-amber-500 px-4 py-2 font-semibold text-white hover:bg-amber-400 disabled:opacity-50"
              >
                {loading ? "Saving..." : editingReview ? "Save Changes" : "Submit"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ALL REVIEWS MODAL */}
      {showAllReviews && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="relative w-[92vw] max-w-3xl rounded-2xl bg-white dark:bg-slate-950 transition-colors p-5 shadow-2xl">
            <button onClick={() => setShowAllReviews(false)} className="absolute right-2 top-2 text-gray-500 hover:text-black">✕</button>
            <h3 className="mb-3 text-lg font-semibold">All Reviews</h3>
            <div className="max-h-[500px] divide-y overflow-y-auto">
              {reviews.length === 0 ? (
                <div className="p-6 text-center text-sm text-slate-600 dark:text-slate-400 transition-colors">No reviews yet.</div>
              ) : (
                reviews.map((r, i) => (
                  <div key={r._id || i} className="flex items-start gap-3 py-3">
                    {r.userId?.profileImage ? (
                      <img
                        src={r.userId.profileImage}
                        alt={r.userId?.name || "User"}
                        className="h-10 w-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-600 text-sm font-bold uppercase text-white">
                        {r.userId?.name?.substring(0, 2) || "U"}
                      </div>
                    )}

                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="font-semibold">{r.userId?.name || "User"}</div>
                          <div className="text-xs text-slate-500 dark:text-slate-400 transition-colors">{timeAgo(r.createdAt)}</div>
                          <div className="ml-3 flex items-center">{renderStars(r.rating || 0)}</div>
                        </div>

                        {/* owner or admin actions */}
                        {(() => {
                          const userId = currentUser?.id;
                          const isOwner = userId && String(r.userId?._id || r.userId) === String(userId);
                          const showActions = isOwner || isAdmin;
                          if (!showActions) return null;
                          return (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => openEditReviewModal(r)}
                                className="rounded border px-2 py-1 text-xs text-slate-700 dark:text-slate-300 transition-colors hover:bg-slate-50 dark:bg-slate-900 transition-colors"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDeleteReview(r)}
                                className="rounded border px-2 py-1 text-xs text-red-600 hover:bg-red-50"
                                disabled={loading}
                              >
                                {loading ? "Deleting..." : "Delete"}
                              </button>
                            </div>
                          );
                        })()}
                      </div>
                      <p className="mt-1 text-sm text-slate-700 dark:text-slate-300 transition-colors">{r.comment}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* SHARE SHEET */}
      {shareOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center sm:items-center"
          role="dialog"
          aria-modal="true"
          aria-labelledby="share-modal-title"
        >
          <button
            type="button"
            onClick={closeShareSheet}
            aria-label="Close"
            className="absolute inset-0 bg-black/50"
          />
          <div className="relative w-full max-w-sm rounded-t-2xl border border-slate-200 bg-white p-5 pt-4 shadow-2xl dark:border-slate-700 dark:bg-slate-950 sm:rounded-2xl">
            <button
              type="button"
              onClick={closeShareSheet}
              aria-label="Close share"
              className="absolute right-3 top-3 rounded-full p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800 dark:hover:text-slate-200"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
                <path d="M6 18L18 6M6 6l12 12" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <h2 id="share-modal-title" className="pr-10 text-base font-bold text-slate-900 dark:text-white">
              Share event
            </h2>
            <p className="mt-1 break-all text-xs text-slate-500 dark:text-slate-400">
              {getEventShareUrl(events?._id || id)}
            </p>
            <div className="mt-4 grid gap-2">
              <button
                type="button"
                onClick={shareOnWhatsApp}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[#25D366] px-4 py-3 text-sm font-bold text-white hover:bg-[#1ebe57]"
              >
                Share on WhatsApp
              </button>
              <button
                type="button"
                onClick={copyShareLink}
                className="inline-flex w-full items-center justify-center rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-bold text-slate-800 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800"
              >
                {shareCopied ? "Link copied" : "Copy link"}
              </button>
            </div>
            <p className="mt-3 text-[11px] leading-relaxed text-slate-400">
              Share link API pe jati hai (image preview). Open hone par event page
              <span className="font-semibold"> showtimebro.com/events/… </span>
              khulegi.
            </p>
          </div>
        </div>
      )}

      {/* LOGIN POPUP */}
      {showPopup && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center"
          role="dialog"
          aria-modal="true"
          aria-labelledby="login-modal-title"
        >
          <button onClick={handleCancel} aria-label="Close" className="absolute inset-0 bg-black/50 transition-opacity" />
          <div className="relative w-[92vw] max-w-sm rounded-2xl border border-amber-100/70 bg-white dark:bg-slate-950 transition-colors shadow-2xl animate-[popIn_180ms_cubic-bezier(0.22,1,0.36,1)]">
            <div className="h-1.5 rounded-t-2xl bg-gradient-to-r from-amber-400 via-amber-500 to-amber-400" />
            <div className="p-5">
              <button
                onClick={handleCancel}
                aria-label="Close"
                className="absolute right-2 top-2 rounded-full p-2 hover:bg-slate-100 dark:bg-slate-900 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-300"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M6 18L18 6M6 6l12 12" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>

              <h2 id="login-modal-title" className="text-lg font-semibold text-slate-900 dark:text-white transition-colors">
                Please log in to continue
              </h2>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-400 transition-colors">You need to be signed in to complete your action.</p>

              <div className="mt-5 flex justify-end gap-3">
                <button
                  onClick={handleCancel}
                  className="rounded-xl border px-4 py-2 text-sm font-semibold text-slate-700 dark:text-slate-300 transition-colors hover:bg-slate-50 dark:bg-slate-900 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleLoginRedirect}
                  className="rounded-xl bg-amber-500 px-4 py-2 text-sm font-semibold text-white shadow-sm dark:shadow-none hover:bg-amber-400"
                >
                  Login
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AuthModal
        open={authOpen}
        onClose={() => setAuthOpen(false)}
        defaultTab={authTab}
        next={nextAfterAuth}
        logoSrc={logo}                         // show company logo at top
        brandName="Show Time Bro"   // fallback name for welcome
        headerVariant="logo"                  // change to "welcome" for "Welcome, <name>"
      />
    </>
  );
}
